import React, { useState, useEffect, useCallback } from 'react'

const STEPS = {
  architecte: [
    {
      targetId: 'step-dashboard-stats',
      titre: 'Bienvenue sur Suiveo BTP',
      texte: 'Ici vous voyez en un coup d\'oeil vos chantiers actifs, les taches en attente et l\'avancement global de vos projets.',
    },
    {
      targetId: 'step-nouveau-chantier',
      titre: 'Creer votre premier chantier',
      texte: 'Cliquez ici pour creer un chantier. Vous pourrez inviter des entreprises par email et un code unique sera genere pour votre client.',
    },
    {
      targetId: 'step-nav-chantiers',
      titre: 'Retrouvez vos chantiers',
      texte: 'Tous vos chantiers sont accessibles ici : avancement, code client, membres. Cliquez pour explorer.',
    },
    {
      targetId: 'step-nav-taches',
      titre: 'Assigner les taches aux entreprises',
      texte: 'Creez une tache, selectionnez le chantier, puis choisissez l\'entreprise responsable (carreleur, peintre, menuisier...). Chaque entreprise ne voit que ses taches. Validez ou signalez des reserves quand le travail est termine.',
    },
    {
      targetId: 'step-help-widget',
      titre: 'L\'aide est toujours la',
      texte: 'Ce bouton est disponible a tout moment pour retrouver les guides, les astuces et relancer ce tutoriel.',
    },
  ],
  entreprise: [
    {
      targetId: 'step-rejoindre',
      titre: 'Bienvenue sur Suiveo BTP',
      texte: 'Votre architecte vous a transmis un code a 6 caracteres. Entrez-le ici pour rejoindre le chantier et acceder a vos taches.',
    },
    {
      targetId: 'step-chantiers-liste',
      titre: 'Vos chantiers actifs',
      texte: 'Une fois rejoint, vos chantiers apparaissent ici avec leur statut, avancement et informations de contact.',
    },
    {
      targetId: 'step-nav-taches',
      titre: 'Vos taches assignees',
      texte: 'L\'architecte vous assigne directement les taches qui vous concernent (pose de carrelage, peinture, menuiserie...). Vous ne voyez que vos taches. Mettez le statut a jour et ajoutez des photos pour tenir l\'architecte informe.',
    },
    {
      targetId: 'step-help-widget',
      titre: 'Besoin d\'aide ?',
      texte: 'Ce bouton est toujours disponible pour vous guider. Cliquez dessus a tout moment pour retrouver les guides.',
    },
  ],
}

const STORAGE_KEY_PREFIX = 'cp_onboarding_done_'

export function isOnboardingDone(role) {
  return localStorage.getItem(STORAGE_KEY_PREFIX + role) === '1'
}

export function resetOnboarding(role) {
  localStorage.removeItem(STORAGE_KEY_PREFIX + role)
}

export function markOnboardingDone(role) {
  localStorage.setItem(STORAGE_KEY_PREFIX + role, '1')
}

export default function OnboardingTour({ role, onTermine }) {
  const steps = STEPS[role] || []
  const [etape, setEtape] = useState(0)
  const [rect, setRect] = useState(null)
  const [visible, setVisible] = useState(true)

  const updateRect = useCallback(() => {
    if (etape >= steps.length) return
    const el = document.getElementById(steps[etape].targetId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setTimeout(() => {
        const r = el.getBoundingClientRect()
        setRect(r)
      }, 350)
    } else {
      // Element non trouve : passer automatiquement a l'etape suivante
      if (etape < steps.length - 1) {
        setEtape(e => e + 1)
      } else {
        terminer()
      }
    }
  }, [etape, steps]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateRect()
    window.addEventListener('resize', updateRect)
    return () => window.removeEventListener('resize', updateRect)
  }, [updateRect])

  const terminer = () => {
    markOnboardingDone(role)
    setVisible(false)
    if (onTermine) onTermine()
  }

  const suivant = () => {
    if (etape < steps.length - 1) {
      setEtape(e => e + 1)
    } else {
      terminer()
    }
  }

  const precedent = () => {
    if (etape > 0) setEtape(e => e - 1)
  }

  if (!visible || steps.length === 0) return null

  const step = steps[etape]
  const padding = 10

  // Calcul position tooltip (au-dessus ou en-dessous selon espace disponible)
  const tooltipH = 170
  const tooltipW = 320
  let tooltipStyle = { position: 'fixed', zIndex: 10001, width: tooltipW }

  if (rect) {
    const spaceBelow = window.innerHeight - rect.bottom - padding
    const spaceAbove = rect.top - padding
    const showAbove = spaceBelow < tooltipH + 20 && spaceAbove > tooltipH + 20

    const centerX = rect.left + rect.width / 2
    const left = Math.max(16, Math.min(centerX - tooltipW / 2, window.innerWidth - tooltipW - 16))

    if (showAbove) {
      tooltipStyle.bottom = window.innerHeight - rect.top + 16
      tooltipStyle.left = left
    } else {
      tooltipStyle.top = rect.bottom + 16
      tooltipStyle.left = left
    }
  } else {
    // Centrer si pas de rect
    tooltipStyle.top = '50%'
    tooltipStyle.left = '50%'
    tooltipStyle.transform = 'translate(-50%, -50%)'
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      {/* Overlay sombre */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 9999 }} />

      {/* Spotlight sur l'element cible */}
      {rect && (
        <div style={{
          position: 'fixed',
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(15,23,42,0.65)',
          zIndex: 10000,
          pointerEvents: 'none',
          border: '2px solid rgba(29,158,117,0.7)',
          transition: 'all 0.3s ease',
        }} />
      )}

      {/* Bulle tooltip */}
      <div style={{
        ...tooltipStyle,
        background: 'white', borderRadius: 14,
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ background: '#1D9E75', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginBottom: 2 }}>
              Etape {etape + 1} sur {steps.length}
            </div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>{step.titre}</div>
          </div>
          <button onClick={terminer} title="Passer le tutoriel" style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
            color: 'white', cursor: 'pointer', padding: '4px 10px', fontSize: 12, fontWeight: 500,
          }}>
            Passer
          </button>
        </div>

        {/* Contenu */}
        <div style={{ padding: '16px 18px' }}>
          <p style={{ fontSize: 14, color: '#4a5568', lineHeight: 1.65, margin: 0 }}>{step.texte}</p>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: '#e2e8f0', margin: '0 18px' }}>
          <div style={{
            height: '100%', background: '#1D9E75', borderRadius: 2,
            width: ((etape + 1) / steps.length * 100) + '%',
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Footer boutons */}
        <div style={{ padding: '12px 18px', display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button
            onClick={precedent}
            disabled={etape === 0}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              background: 'white', color: etape === 0 ? '#a0aec0' : '#4a5568',
              fontSize: 13, fontWeight: 500, cursor: etape === 0 ? 'default' : 'pointer',
            }}
          >
            Precedent
          </button>
          <button
            onClick={suivant}
            style={{
              padding: '8px 22px', borderRadius: 8, border: 'none',
              background: '#1D9E75', color: 'white',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {etape === steps.length - 1 ? 'Terminer' : 'Suivant'}
          </button>
        </div>
      </div>
    </div>
  )
}
